/**
 * 장기 추적 (M7): 회기 흐름 · 목표 추이 · 반복 패턴. 저장된 구조화 자료만 재조합하고 새로운 사실을 만들지 않는다.
 * 반복 패턴은 회기 분석의 정서·인지·행동·관계 항목 중 비슷한 문장이 2개 이상 회기에 나온 것만 보여준다.
 */
import type {
  CounselingGoal,
  CounselingJournal,
  GoalProgress,
  RiskAssessment,
  Session,
  SessionAnalysis,
} from '../schema/v1';
import { RISK_KEYS } from '../schema/v1';
import { RISK_LABELS } from './brief';
import { charSimilarity, lcs } from '../transcript/normalize';

/** 짧은 표현 묶기: 전체 유사도, 또는 짧은 쪽이 거의 그대로 들어 있으면 ("억울함" ~ "억울한 마음") */
function alike(a: string, b: string, threshold: number): boolean {
  if (a === b || charSimilarity(a, b) >= threshold) return true;
  const x = [...a.replace(/\s+/g, '')];
  const y = [...b.replace(/\s+/g, '')];
  const min = Math.min(x.length, y.length);
  return min >= 2 && lcs(x, y) / min >= 0.66;
}

export type FlowRow = {
  sessionId: string;
  sessionNumber: number;
  date: string;
  minutes?: number;
  theme?: string;
  summary?: string;
  risks: string[];
  interventions: string[];
  changes: string[];
  approved: boolean;
};

export function sessionFlow(i: {
  sessions: Session[];
  journals: CounselingJournal[];
  analyses: SessionAnalysis[];
  risks: RiskAssessment[];
}): FlowRow[] {
  const latest = <T extends { sessionId?: string; createdAt: string }>(xs: T[], sid: string) =>
    xs
      .filter((x) => x.sessionId === sid)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      .at(-1);
  return [...i.sessions]
    .filter((s) => !s.deletedAt)
    .sort((a, b) => a.sessionNumber - b.sessionNumber)
    .map((s) => {
      const j = i.journals.filter((x) => x.sessionId === s.id && !x.supersededById).at(-1);
      const a = latest(i.analyses, s.id);
      const r = latest(i.risks, s.id);
      return {
        sessionId: s.id,
        sessionNumber: s.sessionNumber,
        date: s.sessionDate,
        ...(s.durationMinutes ? { minutes: s.durationMinutes } : {}),
        ...((j?.importantIssue ?? s.primaryThemes?.join(', '))
          ? { theme: j?.importantIssue ?? s.primaryThemes!.join(', ') }
          : {}),
        ...(a?.sessionSummary ? { summary: a.sessionSummary } : {}),
        risks: r
          ? RISK_KEYS.filter((k) => r[k].status === 'present' || r[k].status === 'unclear').map(
              (k) => `${RISK_LABELS[k]}${r[k].status === 'unclear' ? '(불분명)' : ''}`,
            )
          : [],
        interventions: [...new Set((a?.interventions ?? []).map((x) => x.interventionName))],
        changes: a?.notableChanges ?? [],
        approved: s.status === 'approved',
      };
    });
}

export type GoalTrend = {
  goal: CounselingGoal;
  cells: { sessionNumber: number; status?: string; proposed?: boolean }[];
};

/** 목표별 회기 상태. 승인된 상태를 우선, 승인 전 제안은 proposed로 표시 */
export function goalTrends(goals: CounselingGoal[], progress: GoalProgress[], sessions: Session[]): GoalTrend[] {
  const ordered = [...sessions].filter((s) => !s.deletedAt).sort((a, b) => a.sessionNumber - b.sessionNumber);
  return goals
    .filter((g) => !g.deletedAt)
    .map((goal) => ({
      goal,
      cells: ordered.map((s) => {
        const ps = progress.filter((p) => p.goalId === goal.id && p.sessionId === s.id && !p.deletedAt);
        const approved = ps.find((p) => p.approvedStatus);
        const proposed = ps.find((p) => p.proposedStatus);
        return approved
          ? { sessionNumber: s.sessionNumber, status: approved.approvedStatus! }
          : proposed
            ? { sessionNumber: s.sessionNumber, status: proposed.proposedStatus!, proposed: true }
            : { sessionNumber: s.sessionNumber };
      }),
    }));
}

export type PatternCategory = 'emotion' | 'cognition' | 'behavior' | 'relationship';
export const PATTERN_LABELS: Record<PatternCategory, string> = {
  emotion: '정서',
  cognition: '인지',
  behavior: '행동',
  relationship: '관계',
};

export type RecurringPattern = {
  category: PatternCategory;
  label: string;
  variants: string[];
  sessionNumbers: number[];
  hypothesis: boolean;
};

/** 비슷한 문장(글자 유사도 0.6 이상)을 묶고, 2개 이상 회기에 나온 묶음만 */
export function recurringPatterns(
  analyses: SessionAnalysis[],
  sessions: Session[],
  threshold = 0.6,
): RecurringPattern[] {
  const num = new Map(sessions.filter((s) => !s.deletedAt).map((s) => [s.id, s.sessionNumber]));
  const groups: { category: PatternCategory; texts: string[]; sessions: Set<number>; hyp: boolean }[] = [];
  const fields: [PatternCategory, keyof SessionAnalysis][] = [
    ['emotion', 'emotions'],
    ['cognition', 'cognitions'],
    ['behavior', 'behaviors'],
    ['relationship', 'relationshipPatterns'],
  ];
  for (const a of analyses) {
    const n = num.get(a.sessionId);
    if (n === undefined) continue;
    for (const [cat, key] of fields) {
      for (const item of (a[key] as { text: string; status: string }[] | undefined) ?? []) {
        const t = item.text.trim();
        if (!t) continue;
        const g = groups.find((x) => x.category === cat && x.texts.some((y) => alike(y, t, threshold)));
        if (g) {
          if (!g.texts.includes(t)) g.texts.push(t);
          g.sessions.add(n);
          if (item.status === 'hypothesis') g.hyp = true;
        } else groups.push({ category: cat, texts: [t], sessions: new Set([n]), hyp: item.status === 'hypothesis' });
      }
    }
  }
  return groups
    .filter((g) => g.sessions.size >= 2)
    .map((g) => ({
      category: g.category,
      label: [...g.texts].sort((a, b) => a.length - b.length)[0]!,
      variants: g.texts,
      sessionNumbers: [...g.sessions].sort((a, b) => a - b),
      hypothesis: g.hyp,
    }))
    .sort((a, b) => b.sessionNumbers.length - a.sessionNumbers.length || a.sessionNumbers[0]! - b.sessionNumbers[0]!);
}
